import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { motion } from 'framer-motion';
import '../experiment_css/tailwind-build.css'; 
import '../experiment_css/index.css'; 
import './LandingPage.css';

// --- TYPEWRITER COMPONENT ---
const TypewriterEffect = () => {
  const words = ["WARPING REALITY", "SELLING MORE", "SAVING HOURS", "DOMINATING ETSY"];
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);
  const currentWord = words[index % words.length]; 

  useEffect(() => {
    const typeSpeed = 80; 
    const deleteSpeed = 50;
    const pauseTime = 2000;

    const timeout = setTimeout(() => {
      if (!reverse && subIndex < currentWord.length) {
        setSubIndex(prev => prev + 1);
      } else if (!reverse && subIndex === currentWord.length) {
        setTimeout(() => setReverse(true), pauseTime); 
      } else if (reverse && subIndex > 0) {
        setSubIndex(prev => prev - 1);
      } else if (reverse && subIndex === 0) {
        setReverse(false);
        setIndex(prev => (prev + 1) % words.length); 
      }
    }, reverse ? deleteSpeed : typeSpeed);

    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, currentWord]);

  return (
    <span className="headline-red">
      {currentWord.substring(0, subIndex)}
      <span className="cursor-blink">|</span>
    </span>
  );
};

const LandingPage = ({ session }) => {
  // --- STATE MANAGEMENT ---
  const [sliderPosition, setSliderPosition] = useState(40);
  const [isExploded, setIsExploded] = useState(false);
  
  // --- PRICING STATE (Moved here correctly) ---
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly', 'quarterly', 'yearly'

  // Pricing Logic
  const getPrice = (plan) => {
      const prices = {
          lite: { monthly: '2.49', quarterly: '2.24', yearly: '1.87' },
          pro: { monthly: '5.99', quarterly: '5.39', yearly: '4.49' },
          agency: { monthly: '19.99', quarterly: '17.99', yearly: '14.99' }
      };
      return prices[plan][billingCycle];
  };

  // --- INTERACTION HANDLERS ---
  const handleMobileInteraction = () => {
    setIsExploded(!isExploded);
  };

  const handleScrollTrigger = () => {
    if (window.innerWidth <= 1024 && !isExploded) {
       setIsExploded(true);
       setTimeout(() => setIsExploded(false), 2500); 
    }
  };

// --- FAQ STATE ---
// Default to 0 (First item open) for Trust Signal
const [activeFaq, setActiveFaq] = useState(0);

const toggleFaq = (index) => {
  // If clicking the active one, close it (null). If clicking a new one, open it.
  setActiveFaq(activeFaq === index ? null : index);
 };

  const faqData = [
    {
      icon: "bi-shield-lock-fill",
      question: "Do you store my designs?",
      answer: "Privacy is our priority. We run a strict 'Ghostbuster' protocol: all uploaded assets and generated mockups are automatically wiped from our GPU servers after 24 hours. Your IP remains yours, and we never use your designs to train AI models."
    },
    {
      icon: "bi-bag-check-fill",
      question: "Can I use these images for commercial purposes?",
      answer: "Yes! You own 100% of the commercial rights to every image you generate, even on the Free plan. You can use them on Shopify, Etsy, Amazon, social media ads, or your portfolio without attribution or restriction."
    },
    {
      icon: "bi-image-fill",
      question: "Are the images high enough quality for Amazon & Shopify?",
      answer: "Absolutely. Our Lite plan generates 1080p HD, while Pro and Agency plans deliver up to 4K (QHD) resolution. This meets and exceeds the 'hover-to-zoom' requirements for Amazon, Etsy, and premium Shopify themes."
    },
    {
      icon: "bi-lightning-charge-fill",
      question: "How is this different from Canva or Photoshop?",
      answer: "Realism and speed. Canva places a flat image on top (looks fake). Photoshop requires manual warping skills (takes hours). FullMockup's V25 Engine uses 3D physics to wrap your design around fabric folds, adjusting lighting and grain instantly."
    }
  ];

  // --- AUTO-SCAN ANIMATION ---
  useEffect(() => {
    let frame;
    let start = null;
    const duration = 4500; 

    const animateSlider = (timestamp) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;

      if (progress < duration) {
        const wave = Math.sin((progress / duration) * Math.PI * 2);
        setSliderPosition(40 + (wave * 55)); 
        frame = requestAnimationFrame(animateSlider);
      } else {
        setSliderPosition(40); 
      }
    };
    
    const timer = setTimeout(() => {
      frame = requestAnimationFrame(animateSlider);
    }, 1000);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, []);



  return (
    <div className="landing-wrapper">
      
      {/* ==========================
          SECTION 1: HERO
         ========================== */}
      <section className="hero-section">
        
        {/* FRAME 1: TEXT CONTENT */}
        <div className="hero-text-frame">
            <h3 className="hero-mini-heading">STOP POSTING FLAT MOCKUPS.</h3>
            
            <div className="heading-wrapper">
                <h1 className="hero-main-heading">
                    <span className="headline-black">START</span> <TypewriterEffect />
                </h1>
            </div>
            
            <p className="hero-subheadline">
                Powered by the <span className="highlight-text">v25 Warp Engine</span>. The only generator that uses 3D Displacement Physics to physically wrap your design around fabric folds—instantly and affordably.
            </p>

            <div className="hero-badges-row">
                <div className="tech-badge"><i className="bi bi-browser-chrome"></i> 100% Browser Based</div>
                <div className="tech-divider"></div>
                <div className="tech-badge"><i className="bi bi-magic"></i> No Photoshop</div>
                <div className="tech-divider"></div>
                <div className="tech-badge"><i className="bi bi-cloud-slash"></i> No Software</div>
                <div className="tech-divider"></div>
                <div className="tech-badge"><i className="bi bi-lightning-charge"></i> Zero Skills</div>
            </div>

            <div className="hero-cta-group">
                <Link to="/app/apparel" className="btn-primary-hero">
                    Create Free Mockup <i className="bi bi-chevron-right"></i>
                </Link>
                <button className="btn-text-hero" onClick={() => document.getElementById('comparison-frame').scrollIntoView({ behavior: 'smooth' })}>
                    See the Difference <i className="bi bi-arrow-down"></i>
                </button>
            </div>
        </div>

        {/* FRAME 2: SLIDER */}
        <motion.div 
            className="hero-visual-frame" 
            id="comparison-frame"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
        >
            <div className="slider-library-wrapper">
                <ReactCompareSlider
                    position={sliderPosition} 
                    onPositionChange={(pos) => setSliderPosition(pos)}
                    handle={
                        <div className="custom-slider-handle">
                            <div className="handle-bar"></div>
                            <div className="handle-knob">
                                <i className="bi bi-caret-left-fill"></i>
                                <i className="bi bi-caret-right-fill"></i>
                            </div>
                        </div>
                    }
                    itemOne={<ReactCompareSliderImage src="/images/showcase/LandingPage/before-slidder-landingPG.webp" alt="Flat Overlay" />}
                    itemTwo={<ReactCompareSliderImage src="/images/showcase/LandingPage/after-slidder-landingPG.webp" alt="Warp Engine" />}
                    onlyHandleDraggable={true} 
                    style={{ height: '100%', width: '100%', borderRadius: '16px' }}
                />
                <div className="slider-label label-left">Others</div>
                <div className="slider-label label-right">v25 Engine</div>
            </div>

            <p className="slider-seo-desc">
                <i className="bi bi-info-circle-fill"></i> <strong>v25 Displacement Logic:</strong> Unlike standard overlay tools, our engine calculates the <span className="highlight-desc">Z-Depth topology</span> of the cloth to bend pixels naturally into every wrinkle, shadow, and texture grain.
            </p>
        </motion.div>
      </section>

      {/* ==========================
          SECTION 2: WORKFLOW BENTO
         ========================== */}
      <section className="workflow-section">
          
          <div className="workflow-header">
              <h2 className="section-title">How It Works</h2>
              <p className="section-subtitle">Photorealistic results in 4 simple steps.</p>
          </div>

          <div className="bento-grid-wrapper">
              
              <div className="thread-overlay">
                    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="thread-svg">       
                      <motion.path 
                            d="M 25 10 C 60 10, 60 35, 75 35 C 40 35, 40 65, 25 65 C 60 65, 60 90, 75 90"
                            fill="none"
                            stroke="#8A181A"
                            strokeWidth="0.5"
                            strokeDasharray="3 3"
                            initial={{ pathLength: 0, opacity: 0.2 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            transition={{ duration: 2.5, ease: "easeInOut" }}
                        />
                  </svg>
              </div>

              <div className="bento-col col-left">
                  <motion.div className="bento-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                      <div className="card-icon-box"><i className="bi bi-cloud-arrow-up-fill"></i></div>
                      <div className="card-content"><span className="step-num">01</span><h3>Upload Base</h3><p>Upload your own blank garment photo to start creating instantly.</p></div>
                  </motion.div>
                  <motion.div className="bento-card card-highlight" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                      <div className="card-icon-box icon-pulse"><i className="bi bi-cpu-fill"></i></div>
                      <div className="card-content"><span className="step-num step-red">03</span><h3 style={{color: '#8A181A'}}>v25 Warp Engine</h3><p>Physics engine calculates z-depth, displacement, and lighting in seconds.</p></div>
                  </motion.div>
              </div>

              <div className="bento-col col-right">
                  <motion.div className="bento-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                      <div className="card-icon-box"><i className="bi bi-layers-fill"></i></div>
                      <div className="card-content"><span className="step-num">02</span><h3>Drop Design</h3><p>Drag & drop your design. We support transparent PNGs for seamless blending.</p></div>
                  </motion.div>
                  <motion.div className="bento-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                      <div className="card-icon-box"><i className="bi bi-image-fill"></i></div>
                      <div className="card-content"><span className="step-num">04</span><h3>Export 4K</h3><p>Download high-res PNGs ready for your store immediately.</p></div>
                  </motion.div>
              </div>

          </div>
      </section>

      {/* ==========================
          SECTION 3: TECH FLEX (Exploded View)
         ========================== */}
      <section className="tech-flex-section">
          
          <div className="tech-content">
              <h2 className="section-title text-left">Why We Are Better Than <br/>Canva & Placeit</h2>
              
              <div className="feature-list">
                  <div className="feature-item">
                      <div className="feature-icon"><i className="bi bi-layers-half"></i></div>
                      <div className="feature-text">
                          <h4>Displacement Mapping</h4>
                          <p>We don't just overlay. We warp pixels to match every wrinkle.</p>
                      </div>
                  </div>
                  <div className="feature-item">
                      <div className="feature-icon"><i className="bi bi-grid-3x3"></i></div>
                      <div className="feature-text">
                          <h4>Texture Blending</h4>
                          <p>Our engine preserves the fabric grain through your ink.</p>
                      </div>
                  </div>
                  <div className="feature-item">
                      <div className="feature-icon"><i className="bi bi-lamp-fill"></i></div>
                      <div className="feature-text">
                          <h4>Smart Shadows</h4>
                          <p>Lighting is recalculated, not just darkened.</p>
                      </div>
                  </div>
              </div>

              <p className="seo-keyword-text">
                  Unlike generic <em>print on demand mockup generators</em>, FullMockup uses a proprietary Python engine hosted on high-performance GPUs.
              </p>
          </div>

          <motion.div 
            className={`tech-visual ${isExploded ? 'touch-active' : ''}`}
            onClick={handleMobileInteraction}
            onViewportEnter={handleScrollTrigger}
            viewport={{ once: true, amount: 0.6 }}
          >
              <div className="exploded-wrapper">
                  <div className="layer layer-base">
                      <img src="/images/showcase/LandingPage/TShirt-blank.webp" alt="Maroon T-Shirt Base" />
                      <span className="layer-label">1. Cotton Jersey Base</span>
                  </div>
                  <div className="layer layer-map">
                      <img src="/images/showcase/LandingPage/tech-layer-map.png" alt="Displacement Map" />
                      <span className="layer-label">2. Z-Depth Displacement</span>
                  </div>
                  <div className="layer layer-design">
                      <img src="/images/showcase/LandingPage/Design_landingPg.webp" alt="Ghost Design" />
                      <span className="layer-label">3. Warped Ink Layer</span>
                  </div>
              </div>
              <div className="visual-hint">
                  <i className="bi bi-hand-index-thumb"></i> 
                  <span className="d-md-none">Click to Explore Layers</span>
                  <span className="d-none d-md-block"> Hover to Explode Layers</span>
              </div>
          </motion.div>

      </section>

      {/* ==========================
          SECTION 4: COMPARISON TABLE
         ========================== */}
      <section className="comparison-section">
          <div className="comparison-container">
              
              <div className="comparison-header">
                  <h2 className="section-title">Pro Quality. <br className="mobile-break" /> Unbeatable Price.</h2>
                  <p className="section-subtitle">Stop overpaying for flat overlays. Get physics-based realism for less.</p>
              </div>

              <div className="table-scroll-wrapper">
                  <table className="comparison-table">
                      <thead>
                          <tr>
                              <th className="sticky-col">Feature</th>
                              <th className="highlight-col">
                                  {/* UPDATED LOGO: Side-by-side, No Gap */}
                                  <div className="th-brand">
                                      <span style={{color: '#8A181A'}}>Full</span>
                                      <span style={{color: '#5F4729'}}>Mockup</span>
                                      <span className="us-badge">Us</span>
                                  </div>
                              </th>
                              <th>Others</th>
                              <th>Photoshop</th>
                          </tr>
                      </thead>
                      <tbody>
                          {/* ROW: VISUAL FIDELITY (Was Realism) */}
                          <tr>
                              <td className="sticky-col">Visual Fidelity</td>
                              <td className="highlight-col"><strong>v25 Warp Engine</strong> <br/><span className="sub-text">Physics-based displacement</span></td>
                              <td>Flat Overlay <br/><span className="sub-text">Fake looking</span></td>
                              <td>Manual Displacement <br/><span className="sub-text">High skill required</span></td>
                          </tr>

                          {/* ROW: COST */}
                          <tr>
                              <td className="sticky-col">Monthly Cost</td>
                              <td className="highlight-col price-text">$1.87 / mo</td>
                              <td>$15 / mo</td>
                              <td>$20.99 / mo</td>
                          </tr>

                          {/* ROW: EASE OF USE (Was Skill Level) */}
                          <tr>
                              <td className="sticky-col">Ease of Use</td>
                              <td className="highlight-col">No Skills Required <br/><span className="sub-text">Auto-fitting</span></td>
                              <td>Low <br/><span className="sub-text">Drag & drop</span></td>
                              <td>High <br/><span className="sub-text">Steep learning curve</span></td>
                          </tr>

                          {/* ROW: PLATFORM (Was Requirement) */}
                          <tr>
                              <td className="sticky-col">Platform</td>
                              <td className="highlight-col">🌐 Browser <br/><span className="sub-text">Works on any device</span></td>
                              <td>🌐 Browser</td>
                              <td>💻 Heavy App <br/><span className="sub-text">PC/Mac Only</span></td>
                          </tr>

                          {/* ROW: GENERATION SPEED (Was Speed) */}
                          <tr>
                              <td className="sticky-col">Generation Speed</td>
                              <td className="highlight-col">⚡ Instant (Seconds)</td>
                              <td>🐢 Slow</td>
                              <td>⏳ Hours (Manual)</td>
                          </tr>

                          {/* ROW: CLOUD ASSETS (Was Storage) */}
                          <tr>
                              <td className="sticky-col">Asset Cloud</td>
                              <td className="highlight-col">✅ Secure Storage</td>
                              <td>❌ No Storage</td>
                              <td>❌ Local Drive Only</td>
                          </tr>

                          {/* ROW: RESOLUTION (Was Max Quality) */}
                          <tr>
                              <td className="sticky-col">Output Resolution</td>
                              <td className="highlight-col">Up to 4K (QHD)</td>
                              <td>1080p (HD)</td>
                              <td>Unlimited (Raster)</td>
                          </tr>
                          
                          {/* ROW: AI SUITE (Was Ai Tools) */}
                          <tr>
                              <td className="sticky-col">AI Suite Access</td>
                              <td className="highlight-col">
                                  <strong>Free Forever!</strong><br/>
                                  <span className="sub-text">Remove BG, Convert & more</span>
                              </td>
                              <td>❌ Limited</td>
                              <td>✅ Firefly (Paid Add-on)</td>
                          </tr>
                      </tbody>
                  </table>
              </div>

              {/* FOOTER NOTE & LINK */}
              <div className="pricing-footer" style={{marginTop: '25px', textAlign: 'center'}}>
                  <p style={{fontSize: '0.8rem', color: '#999', marginBottom: '15px'}}>
                      * Price calculated on Lite plan (Yearly billing).
                  </p>
                  <Link to="/pricing" className="btn-text-hero" style={{justifyContent: 'center', fontSize: '1rem'}}>
                      View Full Pricing & Plans <i className="bi bi-arrow-right"></i>
                  </Link>
              </div>

          </div>
      </section>

      {/* ==========================
          SECTION 5: AI TOOLS SHOWCASE
         ========================== */}
      <section className="tools-section">
          <div className="tools-container">
              
              {/* HEADER */}
              <div className="tools-header">
                  <h2 className="section-title">Power Your Business <br className="mobile-break"/> with Ai Design Tools</h2>
                  <p className="section-subtitle">
                      Stop spending thousands on professional photography. Our Ai suite allows you to prepare, polish, and optimize your product images in seconds.
                  </p>
                  
                  {/* VALUE PROPS (Icons) */}
                  <div className="tools-value-row">
                      <div className="value-item">
                          <i className="bi bi-rocket-takeoff-fill"></i>
                          <span>Faster Time-to-Market</span>
                      </div>
                      <div className="value-divider"></div>
                      <div className="value-item">
                          <i className="bi bi-patch-check-fill"></i>
                          <span>Consistent Branding</span>
                      </div>
                      <div className="value-divider"></div>
                      <div className="value-item">
                          <i className="bi bi-magic"></i>
                          <span>Zero Cost Skills</span>
                      </div>
                  </div>
              </div>

              {/* TOOLS GRID (Scrolls on Mobile) */}
              <div className="tools-scroll-wrapper">
                  <div className="tools-grid">
                      
                      {/* CARD 1: REMOVE BACKGROUND */}
                      <motion.div className="tool-card" whileHover={{ y: -8 }} transition={{ duration: 0.2 }}>
                          <div className="tool-badge">FREE FOREVER</div>
                          <div className="tool-visual">
                              <img src="/images/showcase/ToolsImages/BgRemoveTool.png" alt="Remove Background" />
                          </div>
                          <div className="tool-content">
                              <h3>Remove Background</h3>
                              <p>Instantly isolate products from messy backgrounds. Perfect for clean catalog shots.</p>
                              {/* UPDATED LINK */}
                              <a href="https:/fullmockup.com/app/tool/background-remove" className="btn-tool">
                                  Try Tool <i className="bi bi-arrow-right"></i>
                              </a>
                          </div>
                      </motion.div>

                      {/* CARD 2: REPLACE BACKGROUND */}
                      <motion.div className="tool-card" whileHover={{ y: -8 }} transition={{ duration: 0.2 }}>
                          <div className="tool-badge">FREE FOREVER</div>
                          <div className="tool-visual">
                              <img src="/images/showcase/ToolsImages/BgReplaceTool.png" alt="Replace Background" />
                          </div>
                          <div className="tool-content">
                              <h3>Replace Background</h3>
                              <p>Swap boring studio walls for high-end lifestyle settings or solid brand colors.</p>
                              {/* UPDATED LINK */}
                              <a href="https://fullmockup.com/app/tool/background-replacer" className="btn-tool">
                                  Try Tool <i className="bi bi-arrow-right"></i>
                              </a>
                          </div>
                      </motion.div>

                      {/* CARD 3: BLUR BACKGROUND */}
                      <motion.div className="tool-card" whileHover={{ y: -8 }} transition={{ duration: 0.2 }}>
                          <div className="tool-badge">FREE FOREVER</div>
                          <div className="tool-visual">
                              <img src="/images/showcase/ToolsImages/BgBlurTool.png" alt="Blur Background" />
                          </div>
                          <div className="tool-content">
                              <h3>Blur Background</h3>
                              <p>Create professional depth-of-field (DSLR) effects to focus on your merchandise.</p>
                              {/* UPDATED LINK */}
                              <a href="https://fullmockup.com/app/tool/blur-background" className="btn-tool">
                                  Try Tool <i className="bi bi-arrow-right"></i>
                              </a>
                          </div>
                      </motion.div>

                      {/* CARD 4: CONVERT IMAGE */}
                      <motion.div className="tool-card" whileHover={{ y: -8 }} transition={{ duration: 0.2 }}>
                          <div className="tool-badge">FREE FOREVER</div>
                          <div className="tool-visual icon-visual">
                              <i className="bi bi-file-earmark-arrow-up-fill"></i>
                          </div>
                          <div className="tool-content">
                              <h3>Convert Image</h3>
                              <p>Seamlessly switch formats (JPG, PNG, WebP). Optimize images for fast SEO loading.</p>
                              {/* UPDATED LINK */}
                              <a href="https://fullmockup.com/app/tool/image-converter" className="btn-tool">
                                  Try Tool <i className="bi bi-arrow-right"></i>
                              </a>
                          </div>
                      </motion.div>

                  </div>
              </div>

              {/* FOOTER: EXPLORE MORE & T&C */}
              <div className="tools-footer">
                  
                  {/* New Explore Link */}
                  <div style={{ marginBottom: '20px' }}>
                      <a href="http://localhost:3000/tool" className="btn-text-hero" style={{ justifyContent: 'center', fontSize: '1rem' }}>
                          Explore More Tools <i className="bi bi-grid-fill"></i>
                      </a>
                  </div>

                  <p>
                      <i className="bi bi-info-circle-fill"></i> 
                      Access any tool for free. 
                      <span className="tc-text"> T&C Apply: 10 generations/day for free users. Unlimited for Pro/Agency.</span>
                  </p>
              </div>

          </div>
      </section>

      {/* ==========================
          SECTION 6: PRICING PREVIEW (4 Plans)
         ========================== */}
      <section className="pricing-preview-section">
          <div className="pricing-header">
              <h2 className="section-title">Start for Free. <br className="mobile-break"/> Scale when you Sell.</h2>
              <p className="section-subtitle">Choose the plan that fits your scale. All plans include the V25 Warp Engine.</p>
          </div>

          <div className="pricing-cards-row">
              
              {/* CARD 1: STARTER */}
              <div className="pricing-card">
                  <div className="card-header-price">
                      <h3>Starter</h3>
                      <div className="price-big">$0<span className="period">/mo</span></div>
                      <span className="price-sub">For Trial</span>
                  </div>
                  <ul className="price-features">
                      <li><i className="bi bi-check-circle-fill"></i> <strong>3 Generations / Day</strong></li>
                      <li><i className="bi bi-check-circle-fill"></i> 720p Resolution</li>
                      <li><i className="bi bi-check-circle-fill"></i> Watermarked</li>
                      <li><i className="bi bi-check-circle-fill"></i> JPG Only</li>
                  </ul>
                  <Link to="/app/apparel" className="btn-price btn-outline">Start Free</Link>
              </div>

              {/* CARD 2: LITE */}
              <div className="pricing-card">
                  <div className="card-header-price">
                      <h3>Lite</h3>
                      <div className="price-big">$1.87<span className="period">/mo*</span></div>
                      <span className="price-sub">For New Sellers</span>
                  </div>
                  <ul className="price-features">
                      <li><i className="bi bi-check-circle-fill"></i> <strong>150 Gen / Month</strong></li>
                      <li><i className="bi bi-check-circle-fill"></i> 1080p HD Quality</li>
                      <li><i className="bi bi-check-circle-fill"></i> <strong>No Watermark</strong></li>
                      <li><i className="bi bi-check-circle-fill"></i> 10 Storage Slots</li>
                  </ul>
                  <Link to="/pricing" className="btn-price btn-outline">Get Lite</Link>
              </div>

              {/* CARD 3: PRO (Highlighted) */}
              <div className="pricing-card featured-card">
                  <div className="best-value-badge">MOST POPULAR</div>
                  <div className="card-header-price">
                      <h3>Pro</h3>
                      <div className="price-big">$4.49<span className="period">/mo*</span></div>
                      <span className="price-sub">For Growing Brands</span>
                  </div>
                  <ul className="price-features">
                      <li><i className="bi bi-check-circle-fill"></i> <strong>Unlimited Gen</strong></li>
                      <li><i className="bi bi-check-circle-fill"></i> <strong>1920p FHD</strong></li>
                      <li><i className="bi bi-check-circle-fill"></i> JPG, PNG, WebP</li>
                      <li><i className="bi bi-check-circle-fill"></i> 100 Storage Slots</li>
                  </ul>
                  <Link to="/pricing" className="btn-price btn-filled">Go Pro</Link>
              </div>

              {/* CARD 4: AGENCY */}
              <div className="pricing-card">
                  <div className="card-header-price">
                      <h3>Agency</h3>
                      <div className="price-big">$14.99<span className="period">/mo*</span></div>
                      <span className="price-sub">For Power Users</span>
                  </div>
                  <ul className="price-features">
                      <li><i className="bi bi-check-circle-fill"></i> <strong>Priority GPU</strong></li>
                      <li><i className="bi bi-check-circle-fill"></i> <strong>2K (QHD) Quality</strong></li>
                      <li><i className="bi bi-check-circle-fill"></i> Bulk Processing</li>
                      <li><i className="bi bi-check-circle-fill"></i> 5,000 Slots</li>
                  </ul>
                  <Link to="/pricing" className="btn-price btn-outline">View Agency</Link>
              </div>

          </div>
          
          <p style={{fontSize: '0.8rem', color: '#999', marginTop: '30px'}}>
              * Prices shown are billed yearly. Monthly plans available.
          </p>
      </section>

      {/* ==========================
          SECTION 7: SEO POWER TEXT (Insights)
         ========================== */}
      <section className="seo-text-section">
          
          {/* NEW HEADING */}
          <div className="seo-header">
              <h2 className="section-title">The Science Behind the Sales</h2>
              <p className="section-subtitle">Understanding the tech that powers your business.</p>
          </div>

          <div className="seo-container">
              
              {/* Column 1: Tech Explanation */}
              <div className="seo-column">
                  <div className="seo-icon-box">
                      <i className="bi bi-layers-fill"></i>
                  </div>
                  <h4>What is a Displacement Map?</h4>
                  <p>
                      Unlike a standard 2D overlay (which just pastes an image on top), a displacement map uses the <strong>Z-depth data</strong> of the fabric to physically bend your design's pixels. It detects where the shirt folds, where the light hits, and where the shadows fall, wrapping your ink around the fibers for photorealism.
                  </p>
              </div>

              {/* Column 2: Business Guide */}
              <div className="seo-column">
                  <div className="seo-icon-box">
                      <i className="bi bi-cash-coin"></i>
                  </div>
                  <h4>Starting a T-Shirt Business with $0</h4>
                  <p>
                      You don't need inventory to start. With Print-on-Demand (POD) and FullMockup's <strong>Free Starter Plan</strong>, you can generate professional product images (3 per day) without spending a dime. Create your listing on Etsy or Shopify first, and only pay for production when you actually make a sale.
                  </p>
              </div>

              {/* Column 3: Privacy Promise */}
              <div className="seo-column">
                  <div className="seo-icon-box">
                      <i className="bi bi-shield-fill-check"></i>
                  </div>
                  <h4>Why Privacy Matters</h4>
                  <p>
                      Many cloud tools store your designs to train their AI models. We don't. FullMockup runs a strict <strong>"Ghostbuster" Protocol</strong>: your uploaded assets and generated mockups are automatically wiped from our GPU servers after 24 hours (unless saved to your private slots). Your IP remains yours.
                  </p>
              </div>

          </div>
      </section>

      {/* ==========================
          SECTION 8: FAQ (Simple & Centered)
         ========================== */}
      <section className="faq-section">
          <div className="faq-container">
              
              {/* 1. HEADER */}
              <div className="faq-header">
                  <h2 className="section-title">Frequently Asked Questions</h2>
                  <p className="section-subtitle">Everything you need to know about rights, quality, and privacy.</p>
              </div>

              {/* 2. QUESTIONS LIST */}
              <div className="faq-list">
                  {faqData.map((item, index) => (
                      <div 
                        key={index} 
                        className={`faq-item ${activeFaq === index ? 'active' : ''}`}
                        onClick={() => toggleFaq(index)}
                      >
                          <div className="faq-question">
                              <div className="q-content">
                                  <i className={`bi ${item.icon} faq-trust-icon`}></i>
                                  <h4>{item.question}</h4>
                              </div>
                              <span className="faq-toggle-icon">
                                  <i className="bi bi-chevron-down"></i>
                              </span>
                          </div>
                          <div className="faq-answer">
                              <div className="answer-inner">
                                  <p>{item.answer}</p>
                              </div>
                          </div>
                      </div>
                  ))}
              </div>

              {/* 3. FOOTER (Link to FAQ Page) */}
              <div className="faq-footer">
                  <p>Have more questions?</p>
                  <Link to="/faq" className="btn-text-hero">
                      Check out our more FAQs <i className="bi bi-arrow-right"></i>
                  </Link>
              </div>

          </div>
      </section>

      {/* ==========================
          SECTION 9: FINAL CTA (Premium & Confident)
         ========================== */}
      <section className="final-cta-section">
          <div className="cta-overlay-texture"></div> {/* Subtle noise/texture */}
          
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
              <h2 className="cta-headline">Your Brand Deserves Better Images.</h2>
              <p className="cta-sub">Create studio-quality mockups in seconds.</p>
              
              <div className="cta-action-group">
                  <Link to="/app/apparel" className="btn-final-cta">
                      Launch Generator Now
                  </Link>
                  <span className="cta-reassurance">
                      <i className="bi bi-check2"></i> No credit card required
                  </span>
              </div>
          </motion.div>
      </section>
      
    </div>
  );
};

export default LandingPage;